'use client';
import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Price from '@/components/price/Price';
import ProductTabs from '../blocks/product_tabs/ProductTabs';
import Image from 'next/image';
import WindowSize from '@/utils/windowSize';
import { useCampaign } from '@/app/CampaignContext';
import { useModal, MODAL_TYPES } from './ModalProvider';

export default function CrossSellModalWrapper({
  isOpen,
  onClose,
  variant,
  index,
  addCrossSellProduct,
  removeCrossSellProduct,
  productDetailsBlocks,
  position,
  isVariantAdded
}) {
  const modalRef = useRef(null);
  const windowSize = WindowSize();
  const [onMobile, setOnMobile] = useState();
  const campaign = useCampaign();
  const pricingDetailsData = campaign.pricing_details;
  const { modalUtils, getCloseButton } = useModal();

  const modalIndex = index;

  useEffect(() => {
    if (windowSize.width >= 640) {
      setOnMobile(false);
    } else {
      setOnMobile(true);
    }
  }, [windowSize]);

  useEffect(() => {
    if (!isOpen) return;
    const ref = modalRef.current;

    if (ref) {
      ref.focus();
      // Ensure modal content starts at the top
      ref.scrollTop = 0;
    }
  }, [isOpen, onClose]);

  // Ensure modal scrolls to top when opened
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find the modal container with overflow-auto
      const modalContainer = modalRef.current.closest('.overflow-auto');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Also ensure the modal body itself is at top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  function hideModalAddProduct(e, variant) {
    e.preventDefault();
    addCrossSellProduct(e, variant);

    // Close modal briefly and let the state update, then the user can reopen with new state
    onClose();
  }

  function hideModalRemoveProduct(e, variant) {
    e.preventDefault();
    console.debug('hideModalRemoveProduct called', {
      variant,
      index,
      removeCrossSellProduct
    });
    removeCrossSellProduct(e, variant);

    // Close modal briefly and let the state update, then the user can reopen with new state
    onClose();
  }

  if (!isOpen) return null;

  return modalUtils.createModalWrapper({
    modalType: MODAL_TYPES.crossSell,
    isOpen,
    onClose,
    containerClassName: modalUtils.containerPresets.crossSell.green,

    children: (
      <div ref={modalRef} tabIndex={-1}>
        {getCloseButton(MODAL_TYPES.crossSell, onClose)}
        <div className="sm:flex sm:gap-6 lg:gap-8">
          <div className="relative mx-auto h-[180px] w-[180px] max-w-full sm:mx-0 lg:h-[220px] lg:w-[220px]">
            <Image
              src={variant.image.url}
              alt={variant.productHandle.replace(/\.[^/.]+$/, '')}
              fill
              sizes="(min-width: 1024px) 220px, 180px"
              className="object-cover"
            />
          </div>
          <div className="ml-0 pt-5 sm:ml-8 sm:w-[324px] sm:pt-0 lg:ml-10 lg:w-[347px]">
            <div className="h4">{variant.productTitle}</div>
            <div className="body-s text-dawn-grey">{variant.title}</div>

            <Price
              price={variant.price.amount}
              compareAtPrice={variant.compareAtPrice.amount}
              priceClassOverride="price-r text-orange!"
              crossedPriceClassOverride="price-r-crossed text-dawn-grey!"
            />
            {pricingDetailsData?.price_details_link && (
              <Link
                href={pricingDetailsData?.price_details_link.href}
                className="body-s text-dawn-grey mt-1 flex w-fit grow-0 cursor-pointer items-center underline"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${pricingDetailsData?.price_details_link.title} - Link opens in a new tab`}
              >
                <span className="mr-1">
                  {pricingDetailsData?.price_details_link.title}
                </span>
                <span className="sr-only">Link opens in a new tab</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  data-name="Layer 2"
                  viewBox="0 0 12 12"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <g data-name="Layer 1">
                    <path
                      d="M4.152 7.005 10.685.5M7.056.5h3.63v3.614"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round'
                      }}
                    />
                    <path
                      d="M4.674 2.345H1.5a1 1 0 0 0-1 1v6.348a1 1 0 0 0 1 1h6.348a1 1 0 0 0 1-1V6.519"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round'
                      }}
                    />
                  </g>
                </svg>
              </Link>
            )}
            {variant.useCustomText ? (
              <div
                className="custom-text body-s pt-5 pb-10 pl-4"
                dangerouslySetInnerHTML={{ __html: variant.customText }}
              />
            ) : (
              <div className="pt-2">
                {productDetailsBlocks.map((block, index) => {
                  switch (Object.keys(block)[0]) {
                    case 'product_description':
                      return (
                        <ProductTabs
                          key={`modal-${index}`}
                          block={block}
                          modalIndex={`${modalIndex}-${position}`}
                          renderContext={'cross-sell-modal'}
                        />
                      );
                    default:
                      return (
                        <div
                          key={`modal-${index}`}
                          className="empty-component"
                        ></div>
                      );
                  }
                })}
              </div>
            )}
            {isVariantAdded && !onMobile ? (
              <button
                onClick={(e) => hideModalRemoveProduct(e, variant)}
                className={`btn bg-burnt-cognac hover:border-burnt-cognac-120 hover:bg-burnt-cognac-120 h-[48px] w-full`}
                aria-label={`Remove ${variant.productTitle} - ${variant.title} for $${variant.price.amount}`}
              >
                Remove from order
              </button>
            ) : (
              !isVariantAdded &&
              !onMobile && (
                <button
                  onClick={(e) => hideModalAddProduct(e, variant)}
                  className={`btn bg-burnt-cognac hover:border-burnt-cognac-120 hover:bg-burnt-cognac-120 h-[48px] w-full`}
                  aria-label={`Add ${variant.productTitle} - ${variant.title} for $${variant.price.amount}`}
                >
                  Add to order
                </button>
              )
            )}
          </div>
        </div>
        {onMobile && (
          <div className="bg-parlor-green-95 sticky bottom-0 z-10 -mr-5 -ml-5 w-[calc(100%+2.5rem)] px-5 py-5 shadow-lg sm:hidden">
            {isVariantAdded ? (
              <button
                onClick={(e) => hideModalRemoveProduct(e, variant)}
                className={`btn bg-burnt-cognac hover:border-burnt-cognac-120 hover:bg-burnt-cognac-120 h-[48px] w-full`}
                aria-label={`Remove ${variant.productTitle} - ${variant.title} for $${variant.price.amount}`}
              >
                Remove from order
              </button>
            ) : (
              <button
                onClick={(e) => hideModalAddProduct(e, variant)}
                className={`btn bg-burnt-cognac hover:border-burnt-cognac-120 hover:bg-burnt-cognac-120 h-[48px] w-full`}
                aria-label={`Add ${variant.productTitle} - ${variant.title} for $${variant.price.amount}`}
              >
                Add to order
              </button>
            )}
          </div>
        )}
      </div>
    )
  });
}
